import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
import { useDebouncedCallback } from "use-debounce";
//#endregion
//#region types
import { AutoCompleteInputProps } from "models/propTypes/inputPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { FieldConstants, IconDefinitionConstants } from "utils/constants";
import { DialogEnums } from "models/enums";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
//#endregion
//#region components
import Icon from "../../icon";
import InputField from "./inputField";
import { Grid, CircularProgress, Autocomplete } from "@mui/material";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const AutoCompleteInput: React.FC<AutoCompleteInputProps> = (props) => {
  const itemNone: AutocompleteListItem = {
    value: UtilsHelper.getSelectionNoneText(),
  };

  const [selectedItem, setSelectedItem] = React.useState<AutocompleteListItem | null>(null);
  const [inputValue, setInputValue] = React.useState(props.value ?? "");
  const [isValueFromOptions, setIsValueFromOptions] = React.useState<boolean>(true);

  const [options, setOptions] = React.useState<AutocompleteListItem[]>();
  const [currentAction, setCurrentAction] = React.useState<AutocompleteListItem>();

  const [open, setOpen] = React.useState<boolean>(false);
  const [focused, setFocused] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  // Track if we're currently processing a selection to prevent immediate null clears
  const isProcessingSelectionRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (selectedItem?.value !== props.value) {
      setSelectedItem(
        props.value === undefined
          ? null
          : {
              value: props.value,
            }
      );
    }

    if (inputValue !== props.value) setInputValue(props.value ?? "");
    // Clear processing flag when props.value updates (selection is complete or cleared externally)
    isProcessingSelectionRef.current = false;
  }, [props.value]);

  const onChange = (event: any, newValue: AutocompleteListItem | string | null) => {
    // Guard: NumberFormatField creates synthetic events without stopPropagation
    if (event && typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }

    setOpen(false);
    setFocused(false);
    setLoading(false);
    if (typeof newValue === "string") return;

    // Ignore null onChange calls that happen while we're processing a selection
    // This prevents blurOnSelect from clearing the selection immediately after selection
    if (newValue === null && isProcessingSelectionRef.current) {
      return;
    }

    if (newValue !== null) {
      // Mark that we're processing a selection
      isProcessingSelectionRef.current = true;
      // Update inputValue immediately to show selection (useEffect will sync with props.value later)
      setInputValue(newValue.value);
    } else {
      // Legitimate clear - reset flag
      isProcessingSelectionRef.current = false;
      setInputValue("");
    }

    if (!props.clearInputOnSelection) {
      setSelectedItem(newValue);
    }

    props.onItemSelect(newValue);
  };

  const debouncedInputChange = useDebouncedCallback((value: string, reason: string) => {
    if (reason === "input")
      searchItems(value)
        .then((items) => setOptions(items))
        .finally(() => setLoading(false));

    if (props.freeSolo) {
      if (props.value !== value)
        props.onItemSelect({
          value: value,
          description: "",
          group: "",
          data: "",
        });
    }
  }, FieldConstants.DebounceTime);

  const getSuggestedItems = () => {
    const suggestedItems = props.includeNone ? [itemNone, ...(props.suggestedItems ?? [])] : props.suggestedItems;

    return (
      suggestedItems?.map((x) => ({
        ...x,
        group: x.group ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
      })) ?? []
    );
  };

  const setDefaultOptions = () => {
    searchItems("").then((items) => {
      if (
        JSON.stringify(
          (items ?? []).map((a) => {
            return { id: a.id, val: a.value };
          })
        ) !==
        JSON.stringify(
          (options ?? []).map((a) => {
            return { id: a.id, val: a.value };
          })
        )
      )
        setOptions(items);
    });
  };

  const searchItems = async (value: string) => {
    let searchItems: AutocompleteListItem[] = [];

    if (value === "" && props.suggestedItems) {
      searchItems = getSuggestedItems();
    } else {
      if (props.items)
        if (typeof props.items === "function") {
          searchItems = await props.items(value, props.format ? UtilsHelper.applyMask(value, props.format) : undefined);
        } else {
          const items = props.includeNone ? [itemNone, ...(props.items ?? [])] : (props.items ?? []);
          searchItems = value
            ? items
                .map((x) => ({
                  index: x.value.toLowerCase().indexOf(value.toLowerCase()),
                  item: x,
                }))
                .filter((x) => x.index >= 0)
                .sort((a, b) => a.index - b.index)
                .map((x) => x.item)
            : items;
        }
    }
    return searchItems;
  };

  const onInputChange = (event: object, value: string, reason: string) => {
    setLoading(true);
    if (focused) {
      setOpen(true);
    } else {
      setOpen(false);
    }

    setIsValueFromOptions(reason === "reset");

    setInputValue(value);
    debouncedInputChange(value, reason);
  };

  const onOpen = () => {
    if (!focused) {
      setDefaultOptions();
    }
  };

  const onClose = () => {
    setOpen(false);
    props.onClose && props.onClose();
  };

  const onFocus = () => {
    setFocused(true);
    setOpen(true);
    if (!options?.length) {
      setDefaultOptions();
    }
    props.onFocus && props.onFocus();
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    setOpen(false);
    props.onBlur && props.onBlur(event);
  };

  const onActionClicked = (option: AutocompleteListItem, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (!option.enablePropagationOnActionClick) {
      event.stopPropagation();
    }
    setLoading(true);
    setCurrentAction(option);

    if (option.form) {
      dispatch(
        displayDialog({
          isOpen: false,
          id: option.title ?? "autoCompleteActionDialog",
          title: option.title ?? "",
          subTitle: option.subtitle ?? "",
          titleIcon: option.formIcon,
          dialogType: DialogEnums.DialogType.Content,
          actions: [
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
            },
          ],
          children: option.form,
          onSubmit: (value: any) => {
            setOpen(false);
            setLoading(false);
            setCurrentAction(undefined);
            props.onItemSelect(value as AutocompleteListItem);
          },
          onClose: () => {
            setFocused(true);
            setCurrentAction(undefined);
            setLoading(false);
          },
          maxWidth: "sm",
        })
      );
    } else {
      option.onActionClicked &&
        option.onActionClicked(inputValue).then((res) => {
          setLoading(false);
          if (res) {
            option.onActionSuccess && option.onActionSuccess(res);
            if (option.actionSuccessMessage) {
              setTimeout(() => {
                setActionResult(res);
              }, 500);
            } else {
              setActionResult(res);
            }
          }
        });
    }
  };

  const setActionResult = (result: AutocompleteListItem) => {
    setOpen(false);
    setCurrentAction(undefined);
    props.onItemSelect(result);
  };

  const onClick = () => {
    if (focused && !open) {
      setDefaultOptions();
      setOpen(true);
    }
  };

  return (
    <Autocomplete
      id={`autoComplete-${props.name}`}
      freeSolo={props.freeSolo || props.hideDropdownIcon}
      options={(options ?? getSuggestedItems()).concat(props.actions ?? [])}
      getOptionLabel={(option) => (typeof option !== "string" && !props.clearInputOnSelection ? `${option.value}` : "")}
      classes={{
        root: cssExports.autoCompleteRoot,
        popper: clsx(cssExports.autoCompletePopper, props.classes?.popper),
        paper: cssExports.autoCompleteContainer,
        option: cssExports.autoCompleteOption,
        groupLabel: cssExports.autoCompleteGroupTitle,
        noOptions: cssExports.autoCompleteNoOptions,
        inputRoot: clsx(
          props.disabled || props.success || props.error || (props.showLoading && loading)
            ? cssExports.autoCompleteInputWithEndAdornment
            : "",
          cssExports.autoCompleteInputRoot
        ),
        endAdornment: props.disabled ? cssExports.hidden : cssExports.autoCompleteEndAdornment,
        clearIndicator: cssExports.autoCompleteClearIndicator,
        popperDisablePortal: cssExports.popperDisabledPortal,
        popupIndicator: cssExports.autoCompletePopUpIndicator,
      }}
      getOptionDisabled={(option) =>
        !!option.disabled || (currentAction !== undefined && currentAction?.id === option.id)
      }
      value={selectedItem}
      open={open}
      blurOnSelect
      onOpen={onOpen}
      onClose={onClose}
      onChange={onChange}
      onInputChange={onInputChange}
      disabled={props.disabled}
      disableClearable={props.disableClear}
      renderOption={(elements, option) => {
        const matches = match(option.value, inputValue);
        const parts = parse(option.value, matches);
        const selectedAction = currentAction && currentAction?.id === option.id;
        return (
          <li {...elements}>
            {option.onActionClicked || option.form ? (
              <Grid
                key={option.id}
                onClick={onActionClicked.bind(null, option)}
                className={clsx(cssExports.actionItemContainer, option.className)}
              >
                <Grid container className={clsx(cssExports.actionTitleOuter)}>
                  <Grid item>
                    {selectedAction && loading ? (
                      <CircularProgress size={20} className={cssExports.buttonProgressWhite} />
                    ) : selectedAction ? (
                      <span className={clsx(cssExports.actionIcon, cssExports.success)}>
                        <Icon icon={IconDefinitionConstants.WCheck} />
                      </span>
                    ) : option.icon ? (
                      <span className={cssExports.actionIcon}>
                        <Icon icon={option.icon} />
                      </span>
                    ) : (
                      <></>
                    )}
                  </Grid>
                  <Grid item xs>
                    <Grid item className={cssExports.actionTitle}>
                      {currentAction?.actionSuccessMessage && selectedAction && !loading
                        ? currentAction.actionSuccessMessage
                        : option.value.replace(FieldConstants.ValueTemplateText, inputValue ?? "")}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ) : props.itemTemplate ? (
              props.itemTemplate(option, parts)
            ) : (
              <Grid key={option.id} container className={option.className}>
                <Grid container wrap="nowrap">
                  {option.icon ? (
                    <span className={cssExports.icon}>
                      <Icon icon={option.icon} />
                    </span>
                  ) : (
                    <></>
                  )}
                  <span className={clsx(cssExports.optionTextContainer, option.icon ? cssExports.hasIcon : "")}>
                    <span className={cssExports.optionText}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          className={clsx(
                            part.highlight ? cssExports.highlightText : "",
                            option.description ? cssExports.withSubText : ""
                          )}
                        >
                          {part.text}
                        </span>
                      ))}

                      {props.valueSuffix ? (
                        <span
                          className={clsx(option.description ? cssExports.withSubText : "")}
                        >{` ${props.valueSuffix}`}</span>
                      ) : (
                        ""
                      )}
                    </span>
                    {option.description && <span className={cssExports.subText}>{option.description}</span>}
                  </span>
                </Grid>
              </Grid>
            )}
          </li>
        );
      }}
      renderGroup={(option) => {
        return (
          <li key={option.key}>
            {!UtilsHelper.isEmpty(option.group) ? (
              <Grid container direction="row" className={cssExports.autoCompleteGroupTitle}>
                <Grid item xs>
                  {props.groupLabelFormatter ? props.groupLabelFormatter(option.group) : option.group}
                </Grid>
                {props.groupRightContent && props.groupRightContent(option.group)}
              </Grid>
            ) : (
              <></>
            )}
            <ul>{option.children}</ul>
          </li>
        );
      }}
      groupBy={(option) => (option.group === undefined ? "" : `${option.group}`)}
      filterOptions={(options) =>
        options.filter(
          (option) =>
            (!props.error || !option.hideWhenInvalid) &&
            (!option.hide || !option.hide(options, inputValue, isValueFromOptions))
        )
      }
      renderInput={(params) => (
        <InputField
          className={props.className}
          label={props.label}
          placeholder={props.placeholder}
          inputBaseProps={{
            value: inputValue,
            name: props.name,
            type: "search",
            error: props.error,
            onBlur: onBlur,
            onFocus: onFocus,
            onClick: onClick,
          }}
          icon={props.icon}
          autoCompleteInputProps={{
            ...params,
            InputProps: {
              ...params.InputProps,
            },
          }}
          disabled={props.disabled}
          success={props.success}
          maxLength={props.maxLength}
          isNumericString={props.isNumericString}
          allowNegative={props.allowNegative}
          decimalPoints={props.decimalPoints}
          suffix={props.valueSuffix}
          format={props.format}
          browserAutocompleteOff
          isLoading={(props.showLoading && loading) || props.isLoading}
        />
      )}
    />
  );
};

export default AutoCompleteInput;
